import { getProductPlaceValue, orderedSpecificationEntries } from "./quotationSpecHelpers";

/**
 * Shared order/line pricing helpers — used by OrderForm and Quotation PDFs
 * so UI and PDF never drift.
 *
 * Line Amount (price):
 *   width_height (non-cloth): price_per_unit × area_sq_ft
 *   width_height (cloth):     price_per_unit × cloth_total_area
 *                               where Area/Unit = A × B (meter)
 *                               A = round(W_in/20): ≤.3→floor, ≥.4→ceil
 *                               B = (H_in+12)/39.5
 *                               and total = Area/Unit × qty
 *   length:                   price_per_unit × length_ft × qty
 *   pieces:                   price_per_unit × qty
 *
 * Order totals:
 *   subtotal (price) = sum(line amounts)
 *   gst              = sum(line_amount × gst% / 100)
 *   grand total      = subtotal + gst + fixing + transport − discount
 *   balance          = max(0, grandTotal − advance)
 *   Total Area (Sq Ft) excludes cloth lines
 */

import { formatQuotationDateTime as formatQuotationDateTimeShared } from "../../../utils/dateFormat";

export type FormulaType = "width_height" | "length" | "pieces" | string | undefined;

export interface LineProductLike {
  price?: number | string;
  price_per_unit?: number | string;
  area?: number | string;
  area_per_unit?: number | string;
  area_uom?: string;
  nos?: number | string;
  gst?: number | string;
  formulaType?: FormulaType;
  isCloth?: boolean;
  is_cloth_?: boolean;
  width?: number | string;
  height?: number | string;
  wh_uom?: string;
  length?: number | string;
  length_uom?: string;
}

const SQFT_PER_SQM = 10.7639;
/** Exact inches per meter (industry cloth formula) */
const INCHES_PER_METER = 39.3700787;

export function parseNum(v: number | string | undefined | null, fallback = 0): number {
  if (v === undefined || v === null || v === "") return fallback;
  const n = typeof v === "number" ? v : parseFloat(v);
  return isNaN(n) ? fallback : n;
}

/** Normalize UOM aliases used across form, API, and PDFs */
function normalizeLinearUom(uom: string | undefined | null): string {
  const u = (uom || "").trim().toLowerCase();
  if (u === "mm") return "mm";
  if (u === "cm") return "cm";
  if (u === "m" || u === "meter" || u === "metre" || u === "mtr" || u === "mtr.") return "m";
  if (u === "ft" || u === "feet" || u === "foot") return "ft";
  if (u === "in" || u === "inch" || u === "inches") return "in";
  return u;
}

/** Convert a linear dimension to inches (cloth + shared). Guards NaN/empty → 0 */
export function convertLinearToInches(value: number, uom: string): number {
  const v = typeof value === "number" ? value : parseFloat(String(value));
  if (!Number.isFinite(v) || v === 0) return 0;

  switch (normalizeLinearUom(uom)) {
    case "mm":
      return v * 0.0393700787;
    case "cm":
      return v * 0.393700787;
    case "m":
      return v * INCHES_PER_METER;
    case "ft":
      return v * 12;
    case "in":
      return v;
    default:
      // Unknown / empty UOM: treat as inches to avoid silent scale errors on blank
      return v;
  }
}

/**
 * Cloth Area/Unit (industry meter formula):
 *   A_raw = widthInches / 20
 *   A     = round width factor only:
 *             fractional part ≤ 0.3 → previous whole number (floor)
 *             fractional part ≥ 0.4 → next whole number (ceil)
 *             (values between 0.3 and 0.4 also take next whole — material-safe)
 *   B     = (heightInches + 12) / 39.5
 *   Area/Unit (meter) = A × B
 *
 * Examples (width in inches):
 *   80 → 80/20 = 4.0  → A = 4
 *   70 → 70/20 = 3.5  → A = 4
 *   66 → 66/20 = 3.3  → A = 3
 */
export function roundClothWidthFactor(rawA: number): number {
  if (!Number.isFinite(rawA) || rawA <= 0) return 0;

  const whole = Math.floor(rawA + 1e-9);
  const frac = rawA - whole;

  // ≤ 0.3 → keep current whole; > 0.3 (incl. ≥ 0.4) → next whole
  if (frac <= 0.3 + 1e-9) {
    return whole;
  }
  return whole + 1;
}

export function calculateClothAreaPerUnit(
  width: number,
  height: number,
  widthUom: string,
  heightUom?: string
): number {
  const w = typeof width === "number" ? width : parseFloat(String(width));
  const h = typeof height === "number" ? height : parseFloat(String(height));
  if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) return 0;

  const widthInches = convertLinearToInches(w, widthUom);
  const heightInches = convertLinearToInches(h, heightUom || widthUom);
  if (widthInches <= 0 || heightInches <= 0) return 0;

  const A = roundClothWidthFactor(widthInches / 20);
  const B = (heightInches + 12) / 39.5;
  const area = A * B;
  return Number.isFinite(area) ? area : 0;
}

/** Cloth total area for a line = Area/Unit × quantity (Nos) */
export function calculateClothTotalArea(
  width: number,
  height: number,
  widthUom: string,
  heightUom: string | undefined,
  qty: number
): number {
  const q = Number.isFinite(qty) && qty > 0 ? qty : 1;
  const perUnit = calculateClothAreaPerUnit(width, height, widthUom, heightUom);
  return perUnit * q;
}

export function convertLinearToFeet(value: number, uom: string): number {
  switch ((uom || "").trim()) {
    case "MM":
    case "mm":
      return value / 304.8;
    case "CM":
    case "cm":
      return value / 30.48;
    case "Inch":
    case "inch":
    case "in":
      return value / 12;
    case "m":
      return value * 3.28084;
    case "Feet":
    case "ft":
    default:
      return value;
  }
}

export function convertAreaToSquareFeet(area: number, uom: string): number {
  const u = (uom || "").toLowerCase().trim();
  const map: Record<string, number> = {
    "sq.ft": 1,
    "sq ft": 1,
    sqft: 1,
    ft: 1,
    feet: 1,
    "sq.m": SQFT_PER_SQM,
    "sq m": SQFT_PER_SQM,
    sqm: SQFT_PER_SQM,
    "mtr.": SQFT_PER_SQM,
    mtr: SQFT_PER_SQM,
    panels: 1,
    "no's": 1,
    "pcs.": 1,
  };
  return area * (map[u] ?? 1);
}

/** Billable measure used to multiply by price_per_unit */
export function getBillableMeasure(p: LineProductLike): number {
  const formula = p.formulaType || "width_height";
  const qty = parseNum(p.nos, 1) || 1;
  const isCloth = !!(p.isCloth || p.is_cloth_);

  if (formula === "pieces") {
    return qty;
  }

  if (formula === "length") {
    const length = parseNum(p.length);
    const lengthFt = convertLinearToFeet(length, p.length_uom || "m");
    // Prefer stored area (already length×qty in feet) when present
    if (p.area !== undefined && p.area !== null && p.area !== "") {
      return parseNum(p.area);
    }
    return lengthFt * qty;
  }

  // Cloth: industry Area/Unit formula (not sq.ft / sq.m conversion)
  if (isCloth) {
    if (p.area !== undefined && p.area !== null && p.area !== "") {
      return parseNum(p.area);
    }
    if (p.width && p.height) {
      return calculateClothTotalArea(
        parseNum(p.width),
        parseNum(p.height),
        p.wh_uom || "m",
        p.wh_uom || "m",
        qty
      );
    }
    if (p.area_per_unit !== undefined && p.area_per_unit !== null && p.area_per_unit !== "") {
      return parseNum(p.area_per_unit) * qty;
    }
    return 0;
  }

  // width_height (non-cloth)
  let areaSqFt = 0;
  if (p.area !== undefined && p.area !== null && p.area !== "") {
    areaSqFt = convertAreaToSquareFeet(parseNum(p.area), p.area_uom || "Sq.ft");
  } else if (p.width && p.height) {
    const w = convertLinearToFeet(parseNum(p.width), p.wh_uom || "m");
    const h = convertLinearToFeet(parseNum(p.height), p.wh_uom || "m");
    areaSqFt = w * h * qty;
  } else if (p.area_per_unit !== undefined) {
    areaSqFt = convertAreaToSquareFeet(parseNum(p.area_per_unit), p.area_uom || "Sq.ft") * qty;
  }

  return areaSqFt;
}

export function getPricePerUnit(p: LineProductLike): number {
  if (p.price_per_unit !== undefined && p.price_per_unit !== null && p.price_per_unit !== "") {
    return parseNum(p.price_per_unit);
  }
  const amount = parseNum(p.price);
  const measure = getBillableMeasure(p);
  if (measure > 0) return amount / measure;
  return 0;
}

export function calcLineAmount(
  pricePerUnit: number,
  formulaType: FormulaType,
  opts: {
    isCloth?: boolean;
    area?: number;
    areaUom?: string;
    areaPerUnit?: number;
    nos?: number;
    width?: number;
    height?: number;
    whUom?: string;
    length?: number;
    lengthUom?: string;
  }
): number {
  const p: LineProductLike = {
    formulaType,
    isCloth: opts.isCloth,
    area: opts.area,
    area_uom: opts.areaUom,
    area_per_unit: opts.areaPerUnit,
    nos: opts.nos,
    width: opts.width,
    height: opts.height,
    wh_uom: opts.whUom,
    length: opts.length,
    length_uom: opts.lengthUom,
    price_per_unit: pricePerUnit,
  };
  return pricePerUnit * getBillableMeasure(p);
}

export function calcOrderTotals(params: {
  products: LineProductLike[];
  discount?: number | string;
  fixing?: number | string;
  transport?: number | string;
  advance?: number | string;
  advance2?: number | string;
}) {
  // Line `price` is already the full Amount — do NOT multiply by nos again
  const subtotal = params.products.reduce((sum, p) => sum + parseNum(p.price), 0);
  const gst = params.products.reduce(
    (sum, p) => sum + (parseNum(p.price) * parseNum(p.gst)) / 100,
    0
  );
  const discount = parseNum(params.discount);
  const fixing = parseNum(params.fixing);
  const transport = parseNum(params.transport);
  const grandTotal = subtotal + gst + fixing + transport - discount;
  const advance = parseNum(params.advance) + parseNum(params.advance2);
  const balance = Math.max(0, grandTotal - advance);

  return {
    subtotal,
    gst,
    discount,
    fixing,
    transport,
    grandTotal,
    advance,
    balance,
  };
}

/** Total area in Sq Ft for quotation — excludes cloth and non width×height lines */
export function calcTotalAreaSqFt(products: LineProductLike[]): number {
  return products.reduce((sum, p) => {
    const formula = p.formulaType || "width_height";
    if (formula !== "width_height") return sum;
    if (p.isCloth || p.is_cloth_) return sum;

    // Stored `area` is already total (area_per_unit × qty) from ProductForm
    if (p.area !== undefined && p.area !== null && p.area !== "") {
      return sum + convertAreaToSquareFeet(parseNum(p.area), p.area_uom || "Sq.ft");
    }

    if (p.width && p.height) {
      const qty = parseNum(p.nos, 1) || 1;
      const w = convertLinearToFeet(parseNum(p.width), p.wh_uom || "m");
      const h = convertLinearToFeet(parseNum(p.height), p.wh_uom || "m");
      return sum + w * h * qty;
    }

    return sum;
  }, 0);
}

export function formatQuotationDateTime(
  quotationDate?: string | Date | null,
  quotationTime?: string | null,
  fallbackDate?: string | Date | null
): string {
  return formatQuotationDateTimeShared(quotationDate, quotationTime, fallbackDate);
}

/** 16 nool = 1 inch (industry scale used on production copy) */
export const NOOL_PER_INCH = 16;
const QUARTER_INCH = 0.25;

export type ProductionInchFormat = {
  /** Display base inches (entered value for inch UOM; quarter-inch for converted UOMs) */
  displayInches: string;
  /** Sign of nool relative to display base */
  noolSign: "+" | "-" | "";
  /** Absolute nool count (stored for inch UOM; remainder for converted UOMs) */
  noolCount: number;
  /** True physical size in inches (for grouping / compare) */
  totalInches: number;
  isEmpty: boolean;
};

/** Format a quarter-inch multiple without ugly floats (10, 10.5, 10.25, 10.75). */
export function formatQuarterInchDisplay(inches: number): string {
  if (!Number.isFinite(inches)) return "N/A";
  const q = Math.round(inches / QUARTER_INCH) * QUARTER_INCH;
  const cleaned = Math.round(q * 100) / 100;
  const abs = Math.abs(cleaned);
  const whole = Math.floor(abs + 1e-9);
  const frac = Math.round((abs - whole) * 100);
  const sign = cleaned < 0 ? "-" : "";
  if (frac === 0) return `${sign}${whole}`;
  if (frac === 50) return `${sign}${whole}.5`;
  return `${sign}${whole}.${frac.toString().padStart(2, "0")}`;
}

/**
 * Format entered inch value for display without quarter-inch quantization.
 * Keeps readable decimals (strips useless trailing zeros).
 */
export function formatEnteredInchDisplay(inches: number): string {
  if (!Number.isFinite(inches)) return "N/A";
  const cleaned = Math.round(inches * 10000) / 10000;
  if (Number.isInteger(cleaned)) return String(cleaned);
  return String(cleaned)
    .replace(/(\.\d*?[1-9])0+$/, "$1")
    .replace(/\.0+$/, "");
}

/**
 * Production-copy inch display.
 *
 * - When UOM is already Inch: show entered inches + stored nool as-is
 *   (no unit conversion, no 0.25" re-quantization). Matches View Details.
 * - When UOM is Feet/m/cm/mm: convert → inches, then nearest 0.25" + remainder nool
 *   (existing production behavior). Area/price math is unchanged elsewhere.
 *
 * Example (non-inch): 10.35 in via convert path → "10.25" +^{+2}
 * Example (inch UOM): 10.35 + nool 2 → "10.35" +^{+2} (passthrough)
 */
export function formatProductionInchWithNool(
  value: number | string | undefined | null,
  fromUom: string | undefined | null,
  existingNool?: number | string | null
): ProductionInchFormat {
  const empty: ProductionInchFormat = {
    displayInches: "N/A",
    noolSign: "",
    noolCount: 0,
    totalInches: 0,
    isEmpty: true,
  };

  let uom = fromUom;
  if (value === undefined || value === null || value === "") {
    const onlyNool = parseNum(existingNool, 0);
    if (!onlyNool) return empty;
    // Physical size is entirely from nool remainder
    value = 0;
    uom = "in";
  }

  const num = typeof value === "number" ? value : parseFloat(String(value));
  if (!Number.isFinite(num)) return empty;

  const noolExtra = parseNum(existingNool, 0);
  if (num === 0 && !noolExtra) return empty;

  const uomNorm = normalizeLinearUom(uom || "in");

  // --- Inch UOM: display entered value + stored nool directly (no convert / re-quantize) ---
  if (uomNorm === "in") {
    const totalInches = num + (Number.isFinite(noolExtra) ? noolExtra / NOOL_PER_INCH : 0);
    if (!Number.isFinite(totalInches) || (num === 0 && noolExtra === 0)) return empty;
    const noolAbs = Math.abs(noolExtra);
    const noolCount = noolAbs === 0 ? 0 : Math.round(noolAbs);
    return {
      displayInches: formatEnteredInchDisplay(num),
      noolSign: noolExtra > 0 ? "+" : noolExtra < 0 ? "-" : "",
      noolCount,
      totalInches,
      isEmpty: false,
    };
  }

  // --- Other UOMs: convert to inches, then quarter-inch + remainder nool (existing) ---
  let totalInches = convertLinearToInches(num, uom || "in");
  // convertLinearToInches returns 0 for zero input — restore path for nool-only
  if (num === 0) totalInches = 0;
  if (Number.isFinite(noolExtra) && noolExtra !== 0) {
    totalInches += noolExtra / NOOL_PER_INCH;
  }
  if (!Number.isFinite(totalInches)) return empty;
  if (totalInches === 0) return empty;

  // Nearest 0.25" for the displayed base (max |remainder| = 0.125" ≈ 2 nool)
  const quantized = Math.round(totalInches / QUARTER_INCH) * QUARTER_INCH;
  const remainderInches = totalInches - quantized;
  const noolCount = Math.round(Math.abs(remainderInches) * NOOL_PER_INCH);

  if (noolCount === 0) {
    return {
      displayInches: formatQuarterInchDisplay(quantized),
      noolSign: "",
      noolCount: 0,
      totalInches,
      isEmpty: false,
    };
  }

  return {
    displayInches: formatQuarterInchDisplay(quantized),
    noolSign: remainderInches >= 0 ? "+" : "-",
    noolCount,
    totalInches,
    isEmpty: false,
  };
}

/** HTML snippet: `10.25<sup>+2</sup>` (no unit). */
export function productionInchValueHtml(fmt: ProductionInchFormat): string {
  if (fmt.isEmpty) return "N/A";
  const sup =
    fmt.noolCount > 0 ? `<sup>${fmt.noolSign}${fmt.noolCount}</sup>` : "";
  return `${fmt.displayInches}${sup}`;
}

/** HTML snippet: `10.25<sup>+2</sup> Inch` */
export function productionInchToHtml(fmt: ProductionInchFormat, unitLabel = "Inch"): string {
  if (fmt.isEmpty) return "N/A";
  const value = productionInchValueHtml(fmt);
  const unit = String(unitLabel || "").trim();
  return unit ? `${value} ${unit}` : value;
}

export type ProductionCopyProductLike = {
  product_id?: string;
  name?: string;
  place?: string;
  nos?: number | string;
  width?: number | string;
  height?: number | string;
  length?: number | string;
  wh_uom?: string;
  width_uom?: string;
  height_uom?: string;
  length_uom?: string;
  nool_width?: number | string;
  nool_height?: number | string;
  nool_length?: number | string;
  specifications?: Record<string, any>;
  formulaType?: string;
};

export type ProductionCopySizeLine = {
  width: ProductionInchFormat;
  height: ProductionInchFormat;
  length: ProductionInchFormat;
  quantity: number;
  /** Original entered values / UOM (View Details Place display) */
  rawWidth?: string;
  rawHeight?: string;
  rawLength?: string;
  noolWidth?: number | string;
  noolHeight?: number | string;
  noolLength?: number | string;
  whUom?: string;
  lengthUom?: string;
  /** Product formula: length | width_height | pieces | … */
  formulaType?: string;
  /** 1-based order line index(es) that contributed to this row (merged dims → multiple). */
  serialNumbers: number[];
};

export type ProductionCopyGroup = {
  name: string;
  place: string;
  /** Spec identity key (product + place + specs) */
  specKey: string;
  /** Spec entries for display (place excluded; nool_* excluded) */
  specEntries: Array<{ key: string; value: string }>;
  sizes: ProductionCopySizeLine[];
  /** How many original order lines were folded into this group */
  sourceLineCount: number;
};

/** Prefer serial rows when multiple size lines or multiple source lines. */
export function shouldUseProductionSizeLayout(group: ProductionCopyGroup): boolean {
  return group.sizes.length > 1 || group.sourceLineCount > 1;
}

/** Production copy row label from order line serial(s), e.g. "Srl 1" or "Srl 2, 3". */
export function formatProductionSerialLabel(serialNumbers?: number[]): string {
  const nums = (serialNumbers || []).filter((n) => Number.isFinite(n) && n > 0);
  if (nums.length === 0) return "Srl";
  const unique = [...new Set(nums)].sort((a, b) => a - b);
  return `Srl ${unique.join(", ")}`;
}

function mergeProductionSerial(existing: ProductionCopySizeLine, serial: number) {
  const set = new Set(existing.serialNumbers || []);
  set.add(serial);
  existing.serialNumbers = Array.from(set).sort((a, b) => a - b);
}

function minProductionSerial(size: ProductionCopySizeLine): number {
  const nums = size.serialNumbers || [];
  if (nums.length === 0) return Number.MAX_SAFE_INTEGER;
  return Math.min(...nums);
}

/**
 * One dimension line for production copy HTML:
 * `W : 76.5<sup>+1</sup> * H : 54.25<sup>+1</sup> (inch) Qty : 1`
 * Length-only: `Length : … (inch) Qty : 1`
 */
export function buildProductionSizeLineHtml(size: ProductionCopySizeLine): string {
  const qty = size.quantity > 0 ? String(size.quantity) : "N/A";
  const dim = (fmt: ProductionInchFormat) => productionInchValueHtml(fmt);

  if (!size.length.isEmpty && size.width.isEmpty && size.height.isEmpty) {
    return `Length : ${dim(size.length)} (inch) Qty : ${qty}`;
  }

  const bits: string[] = [];
  if (!size.width.isEmpty) bits.push(`W : ${dim(size.width)}`);
  if (!size.height.isEmpty) bits.push(`H : ${dim(size.height)}`);
  if (!size.length.isEmpty) bits.push(`Length : ${dim(size.length)}`);
  if (bits.length === 0) return `Qty : ${qty}`;
  return `${bits.join(" * ")} (inch) Qty : ${qty}`;
}

/** @deprecated use buildProductionSizeLineHtml */
export function buildProductionSizeLineParts(size: ProductionCopySizeLine): string[] {
  return [buildProductionSizeLineHtml(size)];
}

function formatSpecDisplayValue(value: any): string {
  if (value === undefined || value === null || value === "") return "";
  if (typeof value === "object" && value !== null && "name" in value) {
    return String((value as { name?: unknown }).name ?? "");
  }
  return String(value);
}

function isNumericDimNoolKey(key: string): boolean {
  const k = String(key || "")
    .toLowerCase()
    .replace(/\s+/g, "_");
  return (
    k === "nool_width" ||
    k === "nool_height" ||
    k === "nool_length" ||
    k === "noolwidth" ||
    k === "noolheight" ||
    k === "noollength"
  );
}

/** Stable identity for product + place + specs (dimensions excluded). */
export function getProductionProductSpecKey(p: ProductionCopyProductLike): string {
  const idOrName = String(p.product_id || p.name || "")
    .trim()
    .toLowerCase();
  const name = String(p.name || "")
    .trim()
    .toLowerCase();

  let place = String(p.place || "").trim().toLowerCase();
  const specs =
    typeof p.specifications === "string"
      ? (() => {
          try {
            const parsed = JSON.parse(p.specifications);
            return parsed && typeof parsed === "object" && !Array.isArray(parsed)
              ? parsed
              : {};
          } catch {
            return {};
          }
        })()
      : p.specifications || {};
  const specParts: string[] = [];

  for (const key of Object.keys(specs).sort((a, b) => a.localeCompare(b))) {
    const kNorm = key.toLowerCase().replace(/\s+/g, "_");
    if (isNumericDimNoolKey(key)) continue;
    if (kNorm === "place") {
      if (!place) place = formatSpecDisplayValue(specs[key]).trim().toLowerCase();
      continue;
    }
    const display = formatSpecDisplayValue(specs[key]).trim().toLowerCase();
    if (!display) continue;
    specParts.push(`${kNorm}=${display}`);
  }

  return `${idOrName}|${name}|place=${place}|specs=${specParts.join("&")}`;
}

function dimCompareKey(fmt: ProductionInchFormat): string {
  if (fmt.isEmpty) return "";
  return fmt.totalInches.toFixed(6);
}

function getWhUom(p: ProductionCopyProductLike): string {
  return p.wh_uom || p.width_uom || p.height_uom || "in";
}

/**
 * Group order lines for production copy:
 * - Same product + same specs → one block
 * - Same dims within block → merge quantities into one row (serials combined, e.g. Srl 2, 3)
 * - Different dims → separate rows labeled by order line serial (Srl 1, Srl 4, …)
 */
export function groupProductsForProductionCopy(
  products: ProductionCopyProductLike[] | undefined | null
): ProductionCopyGroup[] {
  if (!products || products.length === 0) return [];

  const groups = new Map<
    string,
    {
      group: ProductionCopyGroup;
      sizeMap: Map<string, ProductionCopySizeLine>;
    }
  >();

  for (let lineIndex = 0; lineIndex < products.length; lineIndex++) {
    const p = products[lineIndex];
    const serial = lineIndex + 1;
    const key = getProductionProductSpecKey(p);
    let bucket = groups.get(key);
    if (!bucket) {
      const specEntries: Array<{ key: string; value: string }> = [];
      let place = String(p.place || "").trim();
      for (const [keyName, rawVal] of orderedSpecificationEntries(
        p as Record<string, unknown>
      )) {
        const kNorm = keyName.toLowerCase().replace(/\s+/g, "_");
        if (kNorm === "place") {
          if (!place) place = formatSpecDisplayValue(rawVal).trim();
          continue;
        }
        const display = formatSpecDisplayValue(rawVal).trim();
        if (!display) continue;
        specEntries.push({ key: keyName, value: display });
      }

      bucket = {
        group: {
          name: p.name || "N/A",
          place: place || "",
          specKey: key,
          specEntries,
          sizes: [],
          sourceLineCount: 0,
        },
        sizeMap: new Map(),
      };
      groups.set(key, bucket);
    }

    bucket.group.sourceLineCount += 1;

    const whUom = getWhUom(p);
    const lengthUom = p.length_uom || whUom || "in";
    const width = formatProductionInchWithNool(p.width, whUom, p.nool_width);
    const height = formatProductionInchWithNool(p.height, whUom, p.nool_height);
    const length = formatProductionInchWithNool(
      p.length,
      lengthUom,
      p.nool_length
    );
    const qty = parseNum(p.nos, 0) || 0;
    const sizeKey = [
      dimCompareKey(width),
      dimCompareKey(height),
      dimCompareKey(length),
      String(whUom || "").toLowerCase(),
      String(lengthUom || "").toLowerCase(),
    ].join("|");

    const existing = bucket.sizeMap.get(sizeKey);
    if (existing) {
      existing.quantity += qty;
      mergeProductionSerial(existing, serial);
    } else {
      bucket.sizeMap.set(sizeKey, {
        width,
        height,
        length,
        quantity: qty,
        rawWidth: p.width != null && p.width !== "" ? String(p.width) : "",
        rawHeight: p.height != null && p.height !== "" ? String(p.height) : "",
        rawLength: p.length != null && p.length !== "" ? String(p.length) : "",
        noolWidth: p.nool_width,
        noolHeight: p.nool_height,
        noolLength: p.nool_length,
        whUom,
        lengthUom,
        formulaType: p.formulaType || undefined,
        serialNumbers: [serial],
      });
    }
  }

  const result: ProductionCopyGroup[] = [];
  for (const { group, sizeMap } of groups.values()) {
    group.sizes = Array.from(sizeMap.values()).sort(
      (a, b) => minProductionSerial(a) - minProductionSerial(b)
    );
    result.push(group);
  }
  return result;
}

/** Place from `place` field or a Place entry in specifications. */
export function getProductPlace(
  product: { place?: string; specifications?: unknown } | null | undefined
): string {
  if (!product) return "";
  return getProductPlaceValue(product as Record<string, unknown>);
}

const STATIC_PLACE_LABELS: Record<string, string> = {
  room: "Room",
  hall: "Hall",
  kitchen: "Kitchen",
};

export function formatPlaceLabel(place?: string): string {
  const pl = String(place ?? "").trim();
  if (!pl) return "";
  return STATIC_PLACE_LABELS[pl.toLowerCase()] || pl;
}

/** e.g. Hall - Pleated Mesh Door */
export function formatProductDisplayName(productName: string, place?: string): string {
  const name = String(productName ?? "").trim();
  const pl = formatPlaceLabel(place);
  if (pl && name) return `${pl} - ${name}`;
  return name || pl;
}

export function specificationEntriesExcludingPlace(
  specifications?: Record<string, unknown> | null | string | unknown[],
  specOrder?: string[] | null
): Array<[string, unknown]> {
  return orderedSpecificationEntries({
    specifications,
    specOrder: specOrder ?? undefined,
  });
}
